import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { DENIED, OFFLINE, UNAVAILABLE } from "../data/problems";
import { isSynced } from "../data/progressStore";
import { CheckIcon, DeviceIcon, OfflineIcon } from "./Icons";
import ui from "./ui.module.css";

export const LoadingCards = ({ label = "Loading your lessons…", count = 3 }) => (
  <div className={ui.stack} role="status" aria-live="polite">
    <span className={ui.visuallyHidden}>{label}</span>
    {Array.from({ length: count }, (_, i) => (
      <div key={i} className={ui.skeleton} />
    ))}
  </div>
);

export const Message = ({ title, children, actions }) => (
  <div className={`${ui.card} ${ui.stack}`} role="status">
    <h2 className={ui.cardTitle}>{title}</h2>
    {children && <p>{children}</p>}
    {actions && <div className={ui.stack}>{actions}</div>}
  </div>
);

const COPY = {
  [OFFLINE]: {
    title: "You're offline",
    body: "This needs a connection. Connect to the internet and try again.",
  },
  [UNAVAILABLE]: {
    title: "This lesson isn't ready to teach",
    body: "Part of this lesson is missing. Please let your school know.",
  },
  unknown: {
    title: "Something went wrong",
    body: "Please try again in a moment.",
  },
};

export const ProblemMessage = ({ problem, onRetry, autoRetrying }) => {
  const { signOut } = useAuth();

  if (problem === DENIED) {
    return (
      <Message
        title="Please sign in again"
        actions={
          <button
            type="button"
            className={`${ui.button} ${ui.primary}`}
            onClick={signOut}
          >
            Sign in again
          </button>
        }
      >
        We couldn't confirm you can see these lessons.
      </Message>
    );
  }

  const copy = COPY[problem] || COPY.unknown;
  return (
    <Message
      title={copy.title}
      actions={
        problem === UNAVAILABLE ? (
          <Link to="/" className={`${ui.button} ${ui.secondary}`}>
            Back to Today
          </Link>
        ) : (
          onRetry && (
            <button
              type="button"
              className={`${ui.button} ${ui.primary}`}
              onClick={onRetry}
            >
              Try again
            </button>
          )
        )
      }
    >
      {copy.body}
      {autoRetrying && " We'll keep trying automatically."}
    </Message>
  );
};

export const SavedOnDeviceNotice = ({ children }) => (
  <div className={`${ui.notice} ${ui.warning}`} role="status">
    <OfflineIcon />
    <p>{children}</p>
  </div>
);

export const NotAvailable = () => (
  <Message
    title="This lesson isn't available"
    actions={
      <Link to="/" className={`${ui.button} ${ui.primary}`}>
        Back to Today
      </Link>
    }
  >
    It may have been moved or isn't ready for your class yet.
  </Message>
);

// Deliberately quiet. "Saved on this device" is the honest state until the
// school's records hold the lesson too; neither wording mentions syncing.
export const savedText = (record) =>
  isSynced(record) ? "Saved" : "Saved on this device";

export const SavedLabel = ({ record, size = 16 }) => (
  <>
    {isSynced(record) ? <CheckIcon size={size} /> : <DeviceIcon size={size} />}
    {savedText(record)}
  </>
);
